import { useEffect, useState } from "react";
import api from "@/lib/api";
import { errMsg } from "@/lib/format";
import { useCategories } from "@/hooks/useCategories";
import { toast } from "sonner";
import { Plus, Trash2, Tag, Edit2, Loader2, Check, X, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PALETTE = [
  "#8B5CF6", "#38BDF8", "#10B981", "#22D3EE", "#F59E0B", "#F43F5E",
  "#EC4899", "#EF4444", "#A78BFA", "#94A3B8", "#3B82F6", "#84CC16",
];

export default function CategoriesPage() {
  const { categories, reload } = useCategories();
  const [openAdd, setOpenAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const expenses = categories.filter(c => c.type === "expense");
  const incomes = categories.filter(c => c.type === "income");

  const remove = async (cat) => {
    if (!cat.id || String(cat.id).startsWith("_def_")) {
      toast.info("Esta categoría aún no se ha guardado en tu cuenta. Crea una y elimínala desde ahí.");
      return;
    }
    if (!window.confirm(`¿Eliminar "${cat.name}"? Las recurrentes con esta categoría pasarán a "Otros".`)) return;
    try {
      await api.delete(`/categories/${cat.id}`);
      toast.success("Eliminada");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Personalización</div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1">Categorías</h1>
          <p className="text-sm text-slate-400 mt-1">Crea, renombra y elimina categorías para que reflejen tu vida real.</p>
        </div>
        <button onClick={() => setOpenAdd(true)} data-testid="add-category-btn"
          className="flex items-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-4 py-2.5 rounded-xl font-medium text-sm">
          <Plus className="w-4 h-4"/> Nueva categoría
        </button>
      </div>

      <Section title="Ingresos" icon={ArrowDownToLine} items={incomes} onEdit={setEditing} onDelete={remove} />
      <Section title="Gastos" icon={ArrowUpFromLine} items={expenses} onEdit={setEditing} onDelete={remove} />

      <CategoryDialog open={openAdd} onOpenChange={setOpenAdd} onSaved={reload}/>
      {editing && <CategoryDialog open={true} onOpenChange={(v) => !v && setEditing(null)} initial={editing} onSaved={() => { reload(); setEditing(null); }}/>}
    </div>
  );
}

function Section({ title, icon: Icon, items, onEdit, onDelete }) {
  return (
    <section>
      <h2 className="text-lg font-semibold flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-violet-400"/>{title}</h2>
      {items.length === 0 ? (
        <div className="card-surface p-6 text-center text-slate-500 text-sm">Sin categorías de {title.toLowerCase()}.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map(c => (
            <div key={c.id || c.value} className="card-surface p-4 group relative" data-testid={`cat-card-${c.id || c.value}`}>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${c.color}22` }}>
                  <Tag className="w-4 h-4" style={{ color: c.color }}/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{c.name}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{c.is_default ? "predeterminada" : "personalizada"}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => onEdit(c)} data-testid={`edit-cat-${c.id || c.value}`}
                  className="flex-1 flex items-center justify-center gap-1.5 text-[11px] bg-white/5 hover:bg-violet-500/15 hover:text-violet-200 border border-white/10 px-2 py-1.5 rounded-lg text-slate-300">
                  <Edit2 className="w-3 h-3"/> Editar
                </button>
                <button onClick={() => onDelete(c)} data-testid={`delete-cat-${c.id || c.value}`}
                  className="flex items-center justify-center text-[11px] bg-white/5 hover:bg-rose-500/15 hover:text-rose-300 border border-white/10 px-2 py-1.5 rounded-lg text-slate-300">
                  <Trash2 className="w-3 h-3"/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CategoryDialog({ open, onOpenChange, initial, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [type, setType] = useState(initial?.type || "expense");
  const [color, setColor] = useState(initial?.color || "#8B5CF6");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nombre requerido"); return; }
    setLoading(true);
    try {
      if (initial?.id && !String(initial.id).startsWith("_def_")) {
        await api.patch(`/categories/${initial.id}`, { name: name.trim(), type, color });
        toast.success("Actualizada");
      } else {
        await api.post("/categories", { name: name.trim(), type, color, icon: "Tag" });
        toast.success("Creada");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e2) { toast.error(errMsg(e2)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#15141b] border-white/10 text-white sm:max-w-md" data-testid="category-dialog">
        <DialogHeader><DialogTitle className="text-white">{initial ? "Editar categoría" : "Nueva categoría"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Ej: Mascotas, Niños, Gym" data-testid="cat-name"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-sm"/>

          <div>
            <Lbl>Tipo</Lbl>
            <div className="grid grid-cols-2 gap-2 p-1 bg-white/5 rounded-xl">
              <button type="button" onClick={() => setType("income")} data-testid="cat-type-income"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium ${type === "income" ? "bg-emerald-500/20 text-emerald-300" : "text-slate-400"}`}>
                <ArrowDownToLine className="w-4 h-4"/> Ingreso
              </button>
              <button type="button" onClick={() => setType("expense")} data-testid="cat-type-expense"
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium ${type === "expense" ? "bg-rose-500/20 text-rose-300" : "text-slate-400"}`}>
                <ArrowUpFromLine className="w-4 h-4"/> Gasto
              </button>
            </div>
          </div>

          <div>
            <Lbl>Color</Lbl>
            <div className="grid grid-cols-6 gap-2">
              {PALETTE.map(c => (
                <button key={c} type="button" onClick={() => setColor(c)} data-testid={`cat-color-${c.slice(1)}`}
                  className={`w-9 h-9 rounded-full transition-all ${color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#15141b]" : ""}`} style={{ background: c }}/>
              ))}
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="cat-submit"
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Check className="w-4 h-4"/> {initial ? "Guardar cambios" : "Crear categoría"}</>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Lbl({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">{children}</div>;
}
